import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Link } from 'lucide-react';

/**
 * Displays a compact "incl. fee" badge on transactions that have linked
 * international transaction fees.  On hover/click a popover shows the
 * individual fee rows and the breakdown.
 */
export function LinkedFeeBadge({ transaction }) {
  const fees = transaction?.linked_fees || [];
  if (!fees.length) return null;

  const totalFee = fees.reduce((sum, f) => sum + (f.amount || 0), 0);
  const base     = transaction.amount || 0;
  const total    = transaction.amount_with_fees || base;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-100 text-violet-700 border border-violet-200 cursor-pointer hover:bg-violet-200 transition-colors"
          aria-label={`Includes ${fees.length} linked bank fee(s) totalling ${totalFee.toFixed(2)}`}
          title="Click to view linked bank fees"
        >
          <Link size={9} />
          <span>+fee</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3 bg-white border shadow-lg" side="top">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-xs text-slate-800">Linked Bank Fees</span>
            <span className="text-[10px] text-slate-500">2.75% foreign txn fee</span>
          </div>

          {/* Base transaction */}
          <div className="bg-slate-50 rounded p-2 text-xs">
            <div className="flex justify-between text-slate-600">
              <span>Base transaction</span>
              <span className="tabular-nums font-medium">{base.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>

          {/* Fee rows */}
          <div className="space-y-1">
            {fees.map((fee, i) => (
              <div key={i} className="flex justify-between text-xs text-slate-600 bg-violet-50 rounded px-2 py-1">
                <span className="text-slate-500"># Intl Txn Fee</span>
                <span className="tabular-nums font-medium text-violet-700">
                  +{fee.amount.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                </span>
              </div>
            ))}
          </div>

          {/* Total */}
          <div className="border-t pt-2 flex justify-between text-xs font-semibold">
            <span>Total (incl. fees)</span>
            <span className="tabular-nums text-slate-900">
              {total.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
            </span>
          </div>

          <p className="text-[10px] text-slate-400 leading-relaxed">
            The bank fee has been merged into this transaction for allocation.
            It will be posted to the same GL account as the parent purchase.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
